import {
    Login as LoginIcon,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};

        if (!email) {
            newErrors.email = 'L\'email est requis';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Format d\'email invalide';
        }

        if (!password) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (password.length < 6) {
            newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setLoading(true);
        try {
            // Appel à la fonction login du AuthContext, qui elle-même appelle authService.login
            // La fonction login de AuthContext devrait retourner les données utilisateur si succès
            const loginResult = await login(email, password); // Utilise la fonction login de useAuth()

            // Si loginResult est concluant (par exemple, contient les tokens ou un indicateur de succès)
            // la navigation est gérée par AuthContext ou un composant parent écoutant l'état d'authentification.
            // Cependant, si une navigation explicite est souhaitée ici après le succès :
            if (loginResult && loginResult.success) { // Assurez-vous que loginResult a une propriété comme .success
                console.log("Login.jsx: Login successful via context, navigating to dashboard. User data:", loginResult.user);
                navigate('/dashboard');
            } else {
                // Ce cas peut se produire si login() de AuthContext ne lève pas d'erreur mais ne réussit pas non plus explicitement
                // ou si la structure de loginResult n'est pas celle attendue.
                console.warn("Login.jsx: Login attempt finished, but result was not conclusively successful or structured as expected.", loginResult);
                // Il est possible que l'erreur ait déjà été gérée et affichée par AuthContext ou authService
                // Si ce n'est pas le cas, définir une erreur générique ici.
                if (!errors.general) { // Ne pas écraser une erreur plus spécifique déjà définie
                    setErrors({ general: 'Échec de la connexion. Veuillez réessayer.' });
                }
            }
        } catch (err) {
            console.error('Login.jsx: Error during handleSubmit after calling context login:', err);
            // Gestion d'erreurs améliorée avec messages utilisateur conviviaux
            if (err.response?.status === 401) {
                setErrors({
                    general: 'Identifiants incorrects. Veuillez vérifier votre email et mot de passe.'
                });
            } else if (err.response?.status === 429) {
                setErrors({
                    general: 'Trop de tentatives de connexion. Veuillez attendre quelques minutes avant de réessayer.'
                });
            } else if (err.response?.status === 403) {
                setErrors({
                    general: 'Compte désactivé. Contactez l\'administrateur pour plus d\'informations.'
                });
            } else if (err.response?.status === 400) {
                // Erreur 400 spécifique (Bad Request), souvent due à des données invalides non interceptées par la validation client
                // ou un problème de formatage de la requête que le backend n'attend pas.
                // Les logs du backend indiquent `{"email":["..."]}` pour la 400, donc c'est lié au champ email.
                // authService et AuthContext devraient déjà propager ces erreurs.
                // Si l'erreur contient des détails du backend, les afficher.
                const responseData = err.response?.data;
                if (responseData) {
                    setErrors({
                        email: responseData.email?.[0] || responseData.username?.[0],
                        password: responseData.password?.[0],
                        general: responseData.non_field_errors?.[0] ||
                            responseData.detail ||
                            'Erreur de validation du formulaire côté serveur.'
                    });
                } else {
                    setErrors({ general: 'Erreur de requête. Veuillez vérifier les données saisies.' });
                }
            } else if (err.response?.status >= 500) {
                setErrors({
                    general: 'Erreur serveur temporaire. Veuillez réessayer dans quelques instants.'
                });
            } else if (err.code === 'NETWORK_ERROR' || !err.response) {
                setErrors({
                    general: err.message || 'Problème de connexion réseau. Vérifiez votre connexion internet et réessayez.'
                });
            } else if (err.code === 'TOKEN_STORAGE_ERROR') { // Handle custom error code
                setErrors({
                    general: `Un problème est survenu lors de la sauvegarde de votre session: ${err.message}. Veuillez réessayer.`
                });
            } else {
                // Utiliser le message de l'erreur propagée si disponible
                setErrors({
                    general: err.message || 'Erreur inattendue. Veuillez réessayer ou contacter le support technique.'
                });
            }
        } finally {
            setLoading(false);
        }
    }; const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    }; return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: { xs: 4, sm: 8 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: { xs: 2, sm: 3 },
                    minHeight: { xs: 'calc(100vh - 100px)', sm: 'auto' }
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: { xs: 3, sm: 4, md: 5 },
                        width: '100%',
                        maxWidth: 450,
                        borderRadius: 3,
                        backgroundColor: 'background.paper',
                        boxShadow: {
                            xs: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            sm: '0 10px 25px rgba(0, 0, 0, 0.1)'
                        }
                    }}
                >                    <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                        <Box sx={{
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            p: 2,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <LoginIcon
                                sx={{
                                    color: 'white',
                                    fontSize: { xs: 32, sm: 40 }
                                }}
                            />
                        </Box>
                        <Typography
                            component="h1"
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                textAlign: 'center',
                                fontSize: { xs: '1.5rem', sm: '2rem' }
                            }}
                        >
                            DigitalCloud360
                        </Typography>
                        <Typography
                            variant="h6"
                            color="textSecondary"
                            gutterBottom
                            sx={{
                                textAlign: 'center',
                                mb: 3,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}
                        >
                            Connexion à votre compte
                        </Typography>

                        {errors.general && (
                            <Alert
                                severity="error"
                                sx={{ mt: 2, width: '100%' }}
                                data-testid="login-general-error"
                            >
                                {errors.general}
                            </Alert>
                        )}
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            noValidate
                            sx={{ mt: 1, width: '100%' }}
                            data-testid="login-form"
                        >
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                data-testid="login-email-input"
                                label="Adresse email"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                error={!!errors.email}
                                helperText={errors.email}
                                variant="outlined"
                                inputProps={{
                                    'aria-label': 'Adresse email',
                                    'aria-describedby': errors.email ? 'email-error' : undefined
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2
                                    }
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Mot de passe"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                data-testid="login-password-input"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                error={!!errors.password}
                                helperText={errors.password}
                                variant="outlined"
                                inputProps={{
                                    'aria-label': 'Mot de passe',
                                    'aria-describedby': errors.password ? 'password-error' : undefined
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                                disabled={loading}
                                                size="large"
                                                data-testid="login-password-toggle"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                sx={{
                                    mt: 3,
                                    mb: 2,
                                    height: { xs: 48, sm: 56 },
                                    borderRadius: 3,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                        transform: 'translateY(-1px)'
                                    },
                                    '&:disabled': {
                                        opacity: 0.7
                                    }
                                }}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                                aria-label={loading ? 'Connexion en cours' : 'Se connecter'}
                                data-testid="login-submit-button"
                            >
                                {loading ? 'Connexion en cours...' : 'Se connecter'}
                            </Button>
                            <Box textAlign="center" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Pas encore de compte ?{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/register"
                                        variant="body2"
                                        sx={{ fontWeight: 'bold' }}
                                        data-testid="login-register-link"
                                    >
                                        Inscrivez-vous
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default Login;